import { useMemo } from 'react'

import { useBlock } from './block.hook.js'

/**
 * Reads `block.output` — the slate-kit's per-block "server-computed output"
 * slot — and returns it cast as `T` when it duck-types as react-query
 * `InfiniteData`-shaped (`{ pages: unknown[] }`). When the SSR prefetch
 * didn't run (or ran but couldn't seed this block), the compiler defaults
 * `output` to `{}` and this hook returns `undefined` so callers fall back
 * to their normal client-side fetch.
 *
 * The payload also carries a `prefetchedAt` epoch-ms timestamp stamped at
 * the moment the server-side loader resolved. Callers feed it to react-query
 * via `initialDataUpdatedAt` so the local `staleTime` is measured from the
 * actual fetch time — not from the (later) hydration time. Without this,
 * a remount after react-query GCs the cache would re-read the original
 * `block.output` and stamp it fresh, hiding genuine staleness.
 *
 * Callers pass the payload type they expect (e.g. `InfiniteData<PaginatedPost>`)
 * and feed the result to react-query as `initialData`.
 */
export const useBlockSsrInitialData = <T extends { pages: unknown[] }>():
  | (T & { prefetchedAt?: number })
  | undefined => {
  const { block } = useBlock()
  const output = block?.output
  return useMemo(() => {
    const out = output as T | Record<string, never> | undefined
    return out &&
      typeof out === 'object' &&
      'pages' in out &&
      Array.isArray(out.pages)
      ? (out as T & { prefetchedAt?: number })
      : undefined
  }, [output])
}
